import { formatQuestionnaireAnswer } from '@liza/fhir-utils';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/skeleton';

/** A QR with optional context about which test it came from. HG attaches AOE QRs per-test. */
export type AoeGroup = {
  /** Test name from the source SR's `code.coding[0].display`. Omitted when QR is parent-attached. */
  testName?: string;
  questionnaireResponse: QuestionnaireResponse;
};

export type OrderEntryQuestionsSectionProps = {
  /** All AOE responses for the order, one per source SR. Empty array = no AOEs found. */
  groups: AoeGroup[] | undefined;
  isLoading: boolean;
};

/**
 * Renders Order Entry Questions (AOEs) — answers to lab-specific prompts like "Is the
 * patient fasting?" — from `QuestionnaireResponse`s linked to the lab order via
 * `ServiceRequest.supportingInfo`.
 *
 * Health Gorilla attaches AOE QRs per-test to child SRs (sub-orders), not to the parent
 * order. The orchestrator walks the parent's children and collects all linked QRs;
 * `groups` carries one entry per QR with the originating test name as a sub-heading.
 *
 * Renders nothing when there are no QRs (loading aside) — orders without AOEs are common,
 * an empty state would be noisy.
 */
export const OrderEntryQuestionsSection = ({
  groups,
  isLoading,
}: OrderEntryQuestionsSectionProps): React.ReactElement | null => {
  const { t } = useTranslation('records');

  const headingId = 'order-entry-questions-heading';

  if (isLoading) {
    return (
      <section aria-labelledby={headingId} className="space-y-3">
        <h2 id={headingId} className="text-lg font-semibold">
          {t('orderEntryQuestions')}
        </h2>
        <Skeleton className="h-32 w-full" />
      </section>
    );
  }

  const nonEmptyGroups = (groups ?? []).filter((g) => (g.questionnaireResponse.item ?? []).length > 0);
  if (nonEmptyGroups.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h2 id={headingId} className="text-lg font-semibold">
        {t('orderEntryQuestions')}
      </h2>
      <div className="space-y-4">
        {nonEmptyGroups.map((group, groupIdx) => {
          const items = group.questionnaireResponse.item ?? [];
          return (
            <div key={group.questionnaireResponse.id ?? groupIdx} className="space-y-2">
              {group.testName && <h3 className="text-sm font-semibold text-muted-foreground">{group.testName}</h3>}
              <dl className="space-y-2">
                {items.map((item, idx) => {
                  const label = item.text ?? item.linkId ?? t('questionFallback', { number: idx + 1 });
                  const answers = item.answer ?? [];
                  const formatted = answers
                    .map((a) => formatQuestionnaireAnswer(a))
                    .filter((s): s is string => s !== null);
                  const display = formatted.length > 0 ? formatted.join(', ') : t('noAnswer');
                  return (
                    <div key={`${item.linkId ?? idx}`} className="grid grid-cols-[150px_1fr] gap-3">
                      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
                      <dd className="text-sm">{display}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          );
        })}
      </div>
    </section>
  );
};
